/**
 * Phase 5 — Review (Vorschau vor Export).
 *
 * Production code path:
 *  - GET /api/export/:month/status → counts pro state + export_ready bool
 *  - Birgit sieht in Kanban welche Karten ready (state=calculated) sind
 *  - Aktuell kein expliziter Review-State zwischen calculated und exported
 *
 * Output dieser Phase = Input von Phase 6:
 *   - Liste der Karten die im RZL-Export erscheinen
 *   - Pro Karte: Mitarbeiter + Monat + Totals (aus data.calculation)
 */
import { useEffect, useState } from "react";
import type { WerkstattCardContext } from "../../pages/WerkstattPage";
import { PipelineStepList, type PipelineStep } from "../SettingsTestOcr";
import { PhaseHeader, withUuids } from "./Phase1Upload";

interface ExportStatus {
  month: string;
  client_id: string;
  counts: Record<string, number>;
  total: number;
  ready_for_export: number;
  export_ready: boolean;
}

export function Phase5Review({ ctx }: { ctx: WerkstattCardContext }) {
  const month = ctx.card?.month ?? new Date().toISOString().slice(0, 7);
  const clientId = ctx.card?.client_id ?? "default";
  const [status, setStatus] = useState<ExportStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setStatus(null);
    setErr(null);
    fetch(`api/monthly-batches/${month}/status?client_id=${clientId}`, { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(setStatus)
      .catch(e => setErr(e.message));
  }, [month, clientId]);

  const c = ctx.card;
  const isExpense = c?.kind === "expense" || c?.kind === "unknown";
  // Bei Stundenliste lebt die calculation in ctx.timesheet.data.calculation.
  // Bei Diäten direkt in ctx.card.data.calculation (kein ts-Wrapper).
  const calc = isExpense
    ? (c as any)?.data?.calculation
    : (ctx.timesheet?.data as any)?.calculation;

  const steps: PipelineStep[] = [
    {
      num: 1,
      title: "Karten-Status für den Monat",
      description:
        "GET /api/export/:month/status liefert counts pro state. "
        + "Birgit sieht: wie viele Karten in welchem Zustand. Export erst wenn alle calculated.",
      status: status ? "done" : err ? "error" : "pending",
      detail: status
        ? `${status.total} Karten gesamt · ${status.ready_for_export} calculated · export_ready=${status.export_ready}`
        : err ? `Fehler: ${err}` : "(lädt)",
    },
    {
      num: 2,
      title: "Monats-Total der aktuellen Karte",
      description:
        "data.calculation aus Phase 4 zeigt die Werte die in RZL übergeben werden. "
        + "Birgit prüft pro Karte ob Totals plausibel sind.",
      status: calc ? "done" : "pending",
      detail: calc
        ? renderCalcTotals(calc)
        : c
          ? `Karte hat noch keine calculation (state=${c.state})`
          : "(keine Karte)",
    },
    {
      num: 3,
      title: "Hinweise sichtbar machen",
      description:
        "Plausibilitäts-Hinweise aus Phase 4 (Pflichtpause, Sequenz, Tageshöchst) "
        + "werden vor Export aggregiert angezeigt. Validierung (Phase 3) bedeutet: "
        + "der User hat die Hinweise gesehen und pauschal akzeptiert — sie bleiben "
        + "im data.calculation.warnings persistiert als Audit-Spur, gelten aber "
        + "NICHT mehr als offene Aufgaben (siehe Phase 3 Step 5 Auto-OK).",
      // Post-Validate (state >= validated): Hinweise sind akzeptiert → done.
      // Vor Validate: Anzahl Hinweise als Info, kein error (es ist erwartet
      // dass Hinweise existieren — sie sind dokumentation, nicht Fehler).
      status: c
        ? ["validated", "calculated", "exported", "archived", "superseded"].includes(c.state) ? "done"
          : calc ? "pending"
          : "pending"
        : "pending",
      detail: calc
        ? `${countHints(calc)} Hinweise in der aktuellen Karte ${
            c && ["validated", "calculated", "exported", "archived", "superseded"].includes(c.state)
              ? "(akzeptiert via Validierung)"
              : "(noch nicht akzeptiert — User validiert in Phase 3)"
          }`
        : "(wartet)",
    },
    {
      num: 4,
      title: "Bestätigung 'Bereit für Export'",
      description:
        "**Lücke in Production:** aktuell kein expliziter Review-State. "
        + "Birgit kann direkt von calculated → exported. Vorschlag: optionaler State 'export_ready' "
        + "der Review-Schritt sichtbar macht.",
      // Wenn die Karte schon exportiert wurde, ist dieser implizite Gate "done"
      // (de-facto durchlaufen). Bleibt als optional + pending solange calculated,
      // weil der Gate per Definition nicht persistiert wird.
      status: c?.state === "exported" ? "done" : "pending",
      detail: c?.state === "exported"
        ? "✓ Karte ist exported — Review-Gate implizit passiert"
        : "Spec — kein eigener State zwischen calculated und exported",
      optional: true,
    },
  ];

  return (
    <div>
      <PhaseHeader
        num={5}
        title="Review (Vorschau vor Export)"
        purpose="Letzte menschliche Kontrolle bevor RZL-Datei generiert wird"
        inputs="alle calculated Karten des Monats + ihre data.calculation"
        outputs="implizit: 'go for export' (kein expliziter State heute)"
        prodFiles={[
          "hub/src/routes/export.ts (GET /api/monthly-batches/:month/status)",
          "frontend/src/pages/KanbanPage.tsx (zeigt Monatsstatus)",
        ]}
        userGates="(visuelle Kontrolle, aktuell keine UI-Bestätigung erforderlich)"
      />
      {isExpense && (
        <div style={{
          background: "#dbeafe", border: "1px solid #93c5fd", padding: "8px 12px",
          borderRadius: 4, marginBottom: 12, fontSize: 13,
        }}>
          <strong>Aktive Karte ist Diäten</strong> — die monatsweite Statistik unten zählt
          alle Karten (Stundenliste + Diäten) zusammen. Birgit prüft beim Review nicht eine
          einzelne Karte sondern den Gesamt-Monat: alles auf <code>calculated</code>?
        </div>
      )}
      <PipelineStepList steps={withUuids(c?.id ?? null, "review", steps)} />
      {status && (
        <div className="month-status" style={{ marginTop: 16, fontSize: 13 }}>
          <strong>Monats-Verteilung ({month}):</strong>
          <table style={{ marginTop: 8, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #ddd" }}>
                <th style={{ textAlign: "left", padding: "2px 12px 2px 0" }}>State</th>
                <th style={{ textAlign: "right", padding: "2px 0" }}>Anzahl</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(status.counts).map(([s, n]) => (
                <tr key={s} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: "2px 12px 2px 0" }}><code>{s}</code></td>
                  <td style={{ textAlign: "right", padding: "2px 0" }}>{n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function renderCalcTotals(calc: any): string {
  const t = calc?.totals ?? {};
  // Support both new (calc-abrechnung) and legacy (calc-timesheet) shape
  const ist = t.stunden_ist ?? t.work_hours ?? "?";
  const soll = t.stunden_soll ?? "?";
  const k = t.tage_K ?? t.krank_days ?? "?";
  const u = t.tage_U ?? t.urlaub_days ?? "?";
  const f = t.tage_F ?? t.feiertag_days ?? "?";
  const za = t.stunden_ZA_konsumiert ?? t.zeitausgleich_hours ?? "?";
  return `ist ${ist}h · soll ${soll}h · K ${k}d · U ${u}d · F ${f}d · ZA ${za}h`;
}

function countHints(calc: any): number {
  if (typeof calc?.totals?.hints_count === "number") return calc.totals.hints_count;
  return Array.isArray(calc?.warnings) ? calc.warnings.length : 0;
}
